import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, LoadingIndicator, Row } from "@/components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useCreateUserInvitation } from "@/hooks/mutations/useCreateUserInvitation";
import { useSetNewCompanyUserInCompanyUsersForSettings } from "@/hooks/sets/useSetNewCompanyUserInCompanyUsersForSettings";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CompanySummary, UserInvitationStatus } from "@/types";

const inviteUserSchema = z.object({
  name: z
    .string()
    .min(1, "Required")
    .refine(
      (arg) => {
        const words = arg.trim().split(" ");
        return words.length >= 2;
      },
      {
        message: "First and last name required",
      },
    ),
  email: z.string().min(1, "Required"),
});

export function SettingsInviteUserDialog({
  company,
  currentUserId,
  isOpen,
  setIsOpen,
}: {
  company: CompanySummary;
  currentUserId: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const {
    mutate: createInvitation,
    isPending: isCreateInvitationPending,
    isError: isCreateInvitationError,
    reset: resetCreateInvitation,
  } = useCreateUserInvitation();
  const setNewCompanyUserInCompanyUsersForSettings =
    useSetNewCompanyUserInCompanyUsersForSettings();

  const form = useForm<z.infer<typeof inviteUserSchema>>({
    defaultValues: {
      name: "",
      email: "",
    },
    resolver: zodResolver(inviteUserSchema),
  });

  const handleSubmit = (data: z.infer<typeof inviteUserSchema>) => {
    mixpanelUtils.trackButtonClick("Add user", MIXPANEL_SCREENS.COMPANY);
    const firstName = data.name.split(" ")[0];
    const lastName = data.name.split(" ").slice(1).join(" ");

    createInvitation(
      {
        createUserInvitationRequest: {
          firstName,
          lastName,
          inviteeEmail: data.email,
        },
      },
      {
        onSuccess: () => {
          setNewCompanyUserInCompanyUsersForSettings({
            companyUserToAdd: {
              email: data.email,
              firstName,
              lastName,
              invitationStatus: UserInvitationStatus.PENDING,
              isInvited: true,
              inviterId: currentUserId,
            },
          });
          form.reset();
          setIsOpen(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Reset the form and clear the errors when the dialog is closed.
        form.reset();
        resetCreateInvitation();
        setIsOpen(open);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <Column className="gap-1">
          <p className="text-2xl font-semibold text-slate-700">
            Add a user to {company.name}
          </p>
          <p className="text-sm text-slate-700">
            This will grant full user access to the platform
          </p>
        </Column>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <Column className="gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="First and last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="Email" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </Column>
            <Column className="gap-2">
              <Row className="items-center justify-end gap-2">
                <Button
                  className="rounded-full"
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  className="relative rounded-full"
                  disabled={isCreateInvitationPending}
                  type="submit"
                >
                  <span
                    className={isCreateInvitationPending ? "invisible" : ""}
                  >
                    Add user
                  </span>
                  {isCreateInvitationPending && (
                    <LoadingIndicator
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                      gearColor="#fff"
                      variant="small"
                    />
                  )}
                </Button>
              </Row>
              {isCreateInvitationError && (
                <ErrorText errorMessage="An error occurred while inviting the user. Please try again." />
              )}
            </Column>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
